import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Space from 'webapp/models-custom/space';

export default class FilterSortClient extends FilterSort<Space> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked override sortProp = 'companyName';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		companyName: 3,
		planId: 1,
		createdByName: 1,
	};

	override settingsNamePrefix = 'clientsFilterSettings';
	override trackingObjectName = 'client';

	constructor(options: FilterSortSettings<Space>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Creator
		this.createFilter<number>({
			id: 2,
			label: 'Account manager',
			name: 'user',
			selected: selectedFilters[2] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((ownerId) => {
					if (ownerId === -1) {
						// exception: all others
						return !item.isOwnedByMe;
					} else {
						return item.ownerId === ownerId;
					}
				});
			},
		});

		// Tags (used only)
		this.createFilter<number>({
			id: 1,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[1] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((tagId) => {
					return item.tags.includes(tagId);
				});
			},
		});
	}

	@cached
	get tagOptions(): FilterOption<number>[] {
		const arrayCopy = Array.from(this.mmSpacesService.assignedTags) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};
		this.allItems.forEach((item: Space) => {
			const accountManagerId = item.accountManagerId;
			if (!allItemCountByOwner[accountManagerId]) {
				allItemCountByOwner[accountManagerId] = 0;
			}
			allItemCountByOwner[accountManagerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}
}
